import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, FileText, Building2, Briefcase, Users, Download, Settings, Menu, LogOut, UserCircle, Clock, HardHat } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Shared";
import GlobalSearch from "@/components/GlobalSearch";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/admin/reports", label: "Reports", icon: FileText, id: "reports" },
  { to: "/admin/clients", label: "Clients", icon: Building2, id: "clients" },
  { to: "/admin/projects", label: "Projects", icon: Briefcase, id: "projects" },
  { to: "/admin/employees", label: "Employees", icon: Users, id: "employees" },
  { to: "/admin/timesheets", label: "Timesheets", icon: Clock, id: "timesheets" },
  { to: "/admin/exports", label: "Exports", icon: Download, id: "exports" },
  { to: "/admin/settings", label: "Settings", icon: Settings, id: "settings" },
];

function NavList({ onNavigate }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-white/10 px-5 py-5">
        <div className="rounded-md bg-white p-2"><Logo className="h-12" /></div>
        <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Field Reporting Portal</p>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`admin-nav-${n.id}`}
            className={({ isActive }) => cn("flex h-11 items-center gap-3 rounded-md px-3 text-sm font-semibold transition-colors",
              isActive ? "bg-white text-[#5C241E]" : "text-white/85 hover:bg-white/10 hover:text-white")}>
            <n.icon className="h-[18px] w-[18px]" />{n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 pb-3">
        <Link to="/dashboard" onClick={onNavigate} data-testid="admin-nav-field-work" className="flex h-11 items-center gap-3 rounded-md border border-white/25 px-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">
          <HardHat className="h-[18px] w-[18px]" />My Field Work
        </Link>
        <Link to="/timesheet" onClick={onNavigate} data-testid="admin-nav-my-timesheet" className="mt-1 flex h-11 items-center gap-3 rounded-md border border-white/25 px-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">
          <Clock className="h-[18px] w-[18px]" />My Timesheet
        </Link>
      </div>
      <div className="border-t border-white/10 p-3">
        <Link to="/admin/account" onClick={onNavigate} data-testid="admin-nav-account" className="flex items-center gap-3 rounded-md px-3 py-2 text-white/85 hover:bg-white/10">
          <UserCircle className="h-5 w-5" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
            <p className="truncate text-xs text-white/60">{user?.email}</p>
          </div>
        </Link>
        <button onClick={logout} data-testid="admin-logout-button" className="mt-1 flex h-10 w-full items-center gap-3 rounded-md px-3 text-sm font-semibold text-white/85 hover:bg-white/10">
          <LogOut className="h-4 w-4" />Sign out
        </button>
        <a href="https://builtby-ab.com" target="_blank" rel="noopener noreferrer" data-testid="admin-built-by-ab-link" className="mt-2 block px-3 text-[11px] text-white/70 transition-colors hover:text-white">built by ab</a>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background lg:pl-64">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 bg-[#5C241E] lg:block" data-testid="admin-sidebar">
        <NavList />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-0 bg-[#5C241E] p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <NavList onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-white/95 px-4 backdrop-blur sm:px-8">
        <button className="btn-ghost h-10 w-10 px-0 lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-button" aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </button>
        <GlobalSearch />
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-8 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
